import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { Avatar, Box, Grid, Typography, Stack, Tooltip, IconButton, LinearProgress, Chip, Fade } from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import TotalIncomeCard from 'ui-component/cards/Skeleton/TotalIncomeCard';

// assets
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

// ==============================|| STYLED COMPONENTS ||============================== //

const ProfessionalCardWrapper = styled(MainCard)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  overflow: 'hidden',
  position: 'relative',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  boxShadow: '0 2px 10px 0 rgba(0, 0, 0, 0.03)',
  transition: 'all 0.3s ease',
  minHeight: '160px',
  maxHeight: '160px',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px 0 rgba(0, 0, 0, 0.08)',
    borderColor: '#1241a1' // Deep navy accent on hover
  }
}));

const StatsChip = styled(Chip)(({ theme, trend }) => ({
  height: 24,
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: trend === 'up' ? 'rgba(76, 175, 80, 0.1)' : trend === 'down' ? 'rgba(244, 67, 54, 0.1)' : theme.palette.action.selected,
  color: trend === 'up' ? '#2e7d32' : trend === 'down' ? '#c62828' : theme.palette.text.secondary,
  border: 'none',
  '& .MuiChip-icon': {
    fontSize: '0.875rem',
    color: 'inherit'
  }
}));

const InfoIconButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  color: theme.palette.text.secondary,
  padding: 4,
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover
  }
}));

// ==============================|| UTILITIES ||============================== //

const calculateStats = (customerData) => {
  if (!customerData || customerData.length === 0) {
    return {
      total: 0,
      newThisMonth: 0,
      activeCustomers: 0,
      growthRate: 0,
      trend: 'neutral'
    };
  }

  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  // Calcular nuevos clientes este mes usando createdDate
  const newThisMonth = customerData.filter((customer) => {
    const createdDate = new Date(customer.createdDate);
    return createdDate >= startOfMonth && !isNaN(createdDate);
  }).length;

  // Calcular clientes del mes pasado
  const lastMonthCustomers = customerData.filter((customer) => {
    const createdDate = new Date(customer.createdDate);
    return createdDate >= startOfLastMonth && createdDate < startOfMonth && !isNaN(createdDate);
  }).length;

  // Calcular tasa de crecimiento
  const growthRate =
    lastMonthCustomers > 0 ? (((newThisMonth - lastMonthCustomers) / lastMonthCustomers) * 100).toFixed(1) : newThisMonth > 0 ? 100 : 0;

  // Calcular clientes activos (con actividad en los últimos 30 días)
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const activeCustomers = customerData.filter((customer) => {
    // Un cliente está activo si:
    // 1. Fue creado en los últimos 30 días, o
    // 2. Tiene al menos una reservación (reservations > 0)
    const createdDate = new Date(customer.createdDate);
    const isNewCustomer = createdDate >= thirtyDaysAgo && !isNaN(createdDate);
    const hasReservations = customer.reservations && customer.reservations > 0;

    return isNewCustomer || hasReservations;
  }).length;

  return {
    total: customerData.length,
    newThisMonth,
    activeCustomers,
    growthRate: parseFloat(growthRate),
    trend: growthRate > 0 ? 'up' : growthRate < 0 ? 'down' : 'neutral'
  };
};

// ==============================|| COMPONENTE PRINCIPAL ||============================== //

const TotalCustomersCard = ({ isLoading, customerData }) => {
  const theme = useTheme();
  const [stats, setStats] = useState({
    total: 0,
    newThisMonth: 0,
    activeCustomers: 0,
    growthRate: 0,
    trend: 'neutral'
  });
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (customerData) {
      setStats(calculateStats(customerData));
    }
  }, [customerData]);

  // Calcular porcentaje de clientes activos
  const activePercentage = stats.total > 0 ? ((stats.activeCustomers / stats.total) * 100).toFixed(1) : 0;

  if (isLoading) {
    return <TotalIncomeCard />;
  }

  return (
    <Fade in={true} timeout={500}>
      <ProfessionalCardWrapper border={false} content={false}>
        <Box sx={{ p: 2.5, position: 'relative', height: '100%' }}>
          {/* Botón de información */}
          <Tooltip title="Detalles de clientes">
            <InfoIconButton size="small" onClick={() => setShowDetails(!showDetails)}>
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </InfoIconButton>
          </Tooltip>

          <Grid container spacing={1} height="100%">
            {/* Sección Principal */}
            <Grid item xs={12}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Box flex={1}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      mb: 0.5
                    }}
                  >
                    Total de Clientes
                  </Typography>

                  <Stack direction="row" alignItems="baseline" spacing={1}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        fontSize: '2rem',
                        lineHeight: 1,
                        color: theme.palette.text.primary,
                        letterSpacing: '-0.5px'
                      }}
                    >
                      {stats.total}
                    </Typography>

                    {stats.trend !== 'neutral' && (
                      <StatsChip
                        icon={stats.trend === 'up' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                        label={`${Math.abs(stats.growthRate)}%`}
                        size="small"
                        trend={stats.trend}
                      />
                    )}
                  </Stack>

                  {/* Estadística de nuevos clientes */}
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      mt: 1,
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      fontWeight: 500
                    }}
                  >
                    <PersonAddIcon sx={{ fontSize: 14, mr: 0.5, color: '#10b981' }} />
                    <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600, mr: 0.5 }}>
                      {stats.newThisMonth}
                    </Box> 
                    nuevos este mes
                  </Typography>
                </Box>

                {/* Avatar/Icono */}
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: 'rgba(18, 65, 161, 0.08)',
                    color: '#1241a1',
                    borderRadius: '12px'
                  }}
                >
                  <GroupIcon sx={{ fontSize: 24 }} />
                </Avatar>
              </Stack>
            </Grid>

            {/* Sección de Estadísticas Adicionales */}
            <Grid item xs={12}>
              <Box sx={{ mt: 1.5 }}>
                {/* Barra de progreso de clientes activos */}
                <Stack spacing={0.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.75rem',
                        fontWeight: 500
                      }}
                    >
                      Clientes Activos
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 700,
                        fontSize: '0.75rem'
                      }}
                    >
                      {stats.activeCustomers} <span style={{ color: theme.palette.text.secondary, fontWeight: 500 }}>({activePercentage}%)</span>
                    </Typography>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={parseFloat(activePercentage)}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: theme.palette.grey[200],
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        backgroundColor: '#10b981' // soft teal/green
                      }
                    }}
                  />
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </ProfessionalCardWrapper>
    </Fade>
  );
};

TotalCustomersCard.propTypes = {
  isLoading: PropTypes.bool,
  customerData: PropTypes.array
};

export default TotalCustomersCard;
